import React from 'react';

import {
	SpotlightManager,
	SpotlightPulse,
	SpotlightTarget,
	SpotlightTransition,
} from '@atlaskit/onboarding';
import { type Actions } from '@atlaskit/onboarding/types';
import { Box, xcss } from '@atlaskit/primitives';

import { useChoreographerAssets } from '@confluence/choreographer-services';

import type { PresenceContentMode, PresenceContentType } from '../presenceTypes';

import { usePresenceChangeboarding } from './usePresenceChangeboarding';
import { useTelepointerChangeboarding } from './useTelepointerChangeboarding';

export type PresenceChangeboardingProps = {
	children: React.ReactNode;
	contentId: string;
	contentMode: PresenceContentMode;
	contentType: PresenceContentType;
	totalParticipants: number;
};

export type PresenceChangeboardingFeatureProps = Omit<PresenceChangeboardingProps, 'children'>;

export enum ChangeboardingState {
	INITIAL, // Initial state. No spotlight render.
	STARTED, // Eligibility checks succeeded, starting async controls. No spotlight render yet.
	READY, // Ready to show the pulsing. UI renders spotlights.
	ACTIVE, // Interacted with pulse and ready to show the spotlight. UI renders spotlights.
	FAILED, // If get or set onboarding state calls error out. No spotlight render.
	COMPLETED, // Interacted with spotlight. No spotlight render.
	ABORTED, // Done with the update cycle without success or failure. No spotlight render.
}

const inProgressStates = [
	ChangeboardingState.INITIAL,
	ChangeboardingState.STARTED,
	ChangeboardingState.READY,
	ChangeboardingState.ACTIVE,
];

export type PresenceChangeboardingFeatureResponse = {
	featureName: string;
	state: ChangeboardingState;
	error?: Error;
	content: {
		image: string;
		actions: Actions;
		heading: string;
		label: string;
		description: string;
		onPulseClick: () => void;
		onBlanketClick?: () => void;
	};
};

// The pulse component is wrapping avatar group which has buttons and click events.
// To have the pulse component take precedence, we need to temporarily disable the elements inside.
const disablePointerEventsStyle = xcss({ pointerEvents: 'none' });
const cursorStyle = xcss({ cursor: 'pointer' });
const PointerEvents = ({
	children,
	shouldDisable,
}: {
	children: React.ReactNode;
	shouldDisable: boolean;
}) => {
	if (shouldDisable) {
		return (
			<Box xcss={cursorStyle}>
				<Box xcss={disablePointerEventsStyle}>{children}</Box>
			</Box>
		);
	}

	return <>{children}</>;
};

const VC_IDENTIFIER = 'presence-changeboarding';

export const PresenceChangeboarding = ({
	children,
	contentId,
	contentMode,
	contentType,
	totalParticipants,
}: PresenceChangeboardingProps) => {
	const { Spotlight } = useChoreographerAssets();

	const presenceChangeboarding = usePresenceChangeboarding({
		contentId,
		contentMode,
		contentType,
		totalParticipants,
	});
	const telepointerChangeboarding = useTelepointerChangeboarding({
		contentId,
		contentMode,
		contentType,
		totalParticipants,
	});

	const data = inProgressStates.includes(presenceChangeboarding.state)
		? presenceChangeboarding
		: telepointerChangeboarding;

	if (data.state !== ChangeboardingState.READY && data.state !== ChangeboardingState.ACTIVE) {
		return <Box data-vc={VC_IDENTIFIER}>{children}</Box>;
	}

	return (
		<SpotlightManager blanketIsTinted={false} onBlanketClicked={data.content.onBlanketClick}>
			<SpotlightTarget name="presence-avatar">
				<SpotlightPulse
					testId="presence-pulse"
					data-vc={VC_IDENTIFIER}
					pulse={data.state === ChangeboardingState.READY}
					radius={3}
					onClick={data.content.onPulseClick}
				>
					<PointerEvents shouldDisable={data.state === ChangeboardingState.READY}>
						{children}
					</PointerEvents>
				</SpotlightPulse>
			</SpotlightTarget>

			<SpotlightTransition>
				{data.state === ChangeboardingState.ACTIVE && (
					<Spotlight
						target="presence-avatar"
						key="presence-avatar-spotlight"
						testId="presence-avatar-spotlight"
						image={data.content?.image}
						actions={data.content?.actions}
						heading={data.content?.heading}
						label={data.content?.label}
						dialogPlacement="bottom right"
						targetRadius={3}
						shouldWatchTarget
					>
						{data.content?.description}
					</Spotlight>
				)}
			</SpotlightTransition>
		</SpotlightManager>
	);
};
